import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ProjectTask,
  ProjectTaskStatus,
  ProjectTasksListResponse,
  AssignableUsersResponse,
} from '@stfors/shared-types';
import { api } from '../client';

export function useProjectTasks(projectId: number | null) {
  return useQuery({
    queryKey: ['project-tasks', { project_id: projectId }],
    queryFn: ({ signal }) =>
      api.get<ProjectTasksListResponse>(
        `/api/project-tasks?project_id=${projectId}`,
        signal,
      ),
    enabled: projectId !== null,
  });
}

export function useAssignableUsers() {
  return useQuery({
    queryKey: ['assignable-users'],
    queryFn: ({ signal }) =>
      api.get<AssignableUsersResponse>('/api/users/assignable', signal),
    staleTime: 5 * 60 * 1000,
  });
}

export interface CreateProjectTaskInput {
  project_id: number;
  text: string;
  assignee_user_id?: number;
  status?: ProjectTaskStatus;
  due_date?: number;
}

export function useCreateProjectTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProjectTaskInput) =>
      api.post<{ project_task: ProjectTask }>('/api/project-tasks', input),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: ['project-tasks', { project_id: vars.project_id }],
      });
    },
  });
}

export interface UpdateProjectTaskInput {
  text?: string;
  assignee_user_id?: number | null;
  status?: ProjectTaskStatus;
  due_date?: number | null;
}

export function useUpdateProjectTask(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateProjectTaskInput & { id: number }) =>
      api.patch<{ project_task: ProjectTask }>(`/api/project-tasks/${id}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['project-tasks', { project_id: projectId }],
      });
    },
  });
}

export function useDeleteProjectTask(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: number) =>
      api.delete<void>(`/api/project-tasks/${taskId}`),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['project-tasks', { project_id: projectId }],
      });
    },
  });
}
